// @ts-nocheck

import React, { useCallback, useEffect, useState } from 'react';
import { useRleventsSession } from '@/integrations/rlevents/useRleventsSession';
import { rleventsSupabase } from '@/integrations/rlevents/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

function toTitleCase(snake: string): string {
  return snake
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export default function EventsHubPage() {
  const { session, loading, error, refresh } = useRleventsSession();

  const [data, setData] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setDataLoading(true);
    setDataError(null);
    try {
      const { data: resp, error: invokeError } = await rleventsSupabase.functions.invoke(
        'rle-coach-dashboard'
      );
      // eslint-disable-next-line no-console
      console.log('rle-coach-dashboard response:', resp);
      if (invokeError) {
        setDataError(invokeError.message || 'Failed to load dashboard');
        setDataLoading(false);
        return;
      }
      if (!resp || resp.ok === false) {
        setDataError(resp?.error || 'Dashboard request failed');
        setDataLoading(false);
        return;
      }
      setData(resp);
      setDataLoading(false);
    } catch (err: any) {
      setDataError(err?.message || 'Unknown error fetching dashboard');
      setDataLoading(false);
    }
  }, []);

  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const importTeams = useCallback(async () => {
    setImporting(true);
    setImportMsg(null);
    try {
      const { data: resp, error: invokeError } =
        await rleventsSupabase.functions.invoke('rle-sync-coach-teams', { body: {} });
      if (invokeError || !resp || resp.ok === false) {
        setImportMsg(
          (resp && (resp.error?.message || resp.error)) ||
            invokeError?.message ||
            'Import failed',
        );
      } else {
        const d = resp.data || {};
        setImportMsg(
          `Imported ${d.teams_synced ?? 0} team(s) and ${d.players_synced ?? 0} player(s).`,
        );
        await fetchDashboard();
      }
    } catch (e: any) {
      setImportMsg(e?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [fetchDashboard]);

  useEffect(() => {
    if (session?.user?.id) {
      fetchDashboard();
    }
  }, [session?.user?.id, fetchDashboard]);


  // --- SSO loading state ---
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-8 w-8 rounded-full" />
          <p className="text-sm font-medium text-muted-foreground">
            Connecting to RLevents…
          </p>
        </div>
      </div>
    );
  }

  // --- SSO error state ---
  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4">
        <Card className="w-full max-w-md border-destructive/30">
          <CardHeader>
            <CardTitle className="text-destructive text-lg">Connection Error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
            <Button onClick={refresh} variant="outline" className="w-full">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const counts: Record<string, number> = data?.counts || {};
  const countEntries = Object.entries(counts);

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight">Events Hub</h1>
        <div className="flex flex-col items-end gap-1">
          <Button onClick={importTeams} disabled={importing} variant="outline">
            {importing ? 'Importing…' : 'Import my teams from RecruitLook'}
          </Button>
          {importMsg && (
            <span className="text-xs text-muted-foreground">{importMsg}</span>
          )}
        </div>
      </div>


      {dataLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!dataLoading && dataError && (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-destructive text-lg">
              Failed to load dashboard
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {dataError}
            </div>
            <Button onClick={fetchDashboard} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {!dataLoading && !dataError && data && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {countEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No stats available.</p>
          ) : (
            countEntries.map(([key, value]) => (
              <Card key={key}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {toTitleCase(key)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold tracking-tight">
                    {typeof value === 'number' ? value : String(value ?? 0)}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {!dataLoading && !dataError && (data?.teams?.length ?? 0) > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">My Teams</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.teams.map((t: any) => (
              <div
                key={t.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {t.name}
                    {t.rl_pending && (
                      <span className="ml-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-600 align-middle">
                        Linking to RecruitLook
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[t.age_group, t.gender].filter(Boolean).join(' · ') || 'No division set'}
                    {t.is_registered
                      ? ` · Registered (${t.registration_count})`
                      : ' · Team created — not yet registered'}
                    {t.rl_pending && ' · Roster sync pending'}
                  </p>
                </div>

                {!t.is_registered && (
                  <Button size="sm" asChild>
                    <a href="/events">Register this team</a>
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

    </div>
  );
}
